import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
} from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import { useMutation } from '@tanstack/react-query';
import { setupMFA, verifyMFA } from '../../services/api';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SecurityIcon from '@mui/icons-material/Security';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

interface MFASetupProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const MFASetup = ({ open, onClose, onSuccess }: MFASetupProps) => {
  const [activeStep, setActiveStep] = useState(0);
  const [qrCode, setQrCode] = useState('');
  const [manualKey, setManualKey] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [copiedKey, setCopiedKey] = useState(false);
  const [copiedCodes, setCopiedCodes] = useState(false);

  const steps = ['Install App', 'Scan QR Code', 'Verify Code', 'Save Backup Codes'];

  const setupMutation = useMutation({
    mutationFn: setupMFA,
    onSuccess: (data) => {
      setQrCode(data.secret_code);
      setManualKey(data.manual_entry_key);
      setActiveStep(1);
      setError('');
    },
    onError: (err: any) => {
      console.error('MFA setup error:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to setup MFA';
      setError(errorMessage);
    },
  });

  const verifyMutation = useMutation({
    mutationFn: (code: string) => verifyMFA(code),
    onSuccess: (data) => {
      setBackupCodes(data.backup_codes);
      setActiveStep(3);
      setError('');
    },
    onError: (err: any) => {
      setError(err.response?.data?.error || 'Invalid verification code');
    },
  });

  const handleSetup = () => {
    setupMutation.mutate();
  };

  const handleVerify = () => {
    if (verificationCode.length !== 6) {
      setError('Code must be 6 digits');
      return;
    }
    verifyMutation.mutate(verificationCode);
  };

  const handleCopyKey = () => {
    navigator.clipboard.writeText(manualKey);
    setCopiedKey(true);
    setTimeout(() => setCopiedKey(false), 2000);
  };

  const handleCopyBackupCodes = () => {
    navigator.clipboard.writeText(backupCodes.join('\n'));
    setCopiedCodes(true);
    setTimeout(() => setCopiedCodes(false), 2000);
  };

  const handleComplete = () => {
    onSuccess();
    onClose();
    // Reset state
    setActiveStep(0);
    setQrCode('');
    setManualKey('');
    setVerificationCode('');
    setBackupCodes([]);
    setError('');
  };

  const handleClose = () => {
    if (activeStep === 3) {
      // Force user to save backup codes before closing
      return;
    }
    onClose();
    // Reset state
    setActiveStep(0);
    setQrCode('');
    setManualKey('');
    setVerificationCode('');
    setBackupCodes([]);
    setError('');
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <SecurityIcon color="primary" />
          <Typography variant="h6">Set Up Two-Factor Authentication</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Step 0: Install App */}
        {activeStep === 0 && (
          <Box>
            <Typography variant="body1" gutterBottom>
              First, install an authenticator app on your phone:
            </Typography>
            <List>
              <ListItem>
                <ListItemIcon>
                  <PhoneAndroidIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Google Authenticator"
                  secondary="iOS & Android"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <PhoneAndroidIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Authy"
                  secondary="iOS, Android & Desktop"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <PhoneAndroidIcon />
                </ListItemIcon>
                <ListItemText
                  primary="1Password"
                  secondary="iOS, Android & Desktop"
                />
              </ListItem>
            </List>
            <Alert severity="info" sx={{ mt: 2 }}>
              These apps generate time-based codes that change every 30 seconds.
            </Alert>
          </Box>
        )}

        {/* Step 1: Scan QR Code */}
        {activeStep === 1 && qrCode && (
          <Box>
            <Typography variant="body1" gutterBottom>
              Scan this QR code with your authenticator app:
            </Typography>
            <Paper elevation={0} sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50', my: 2 }}>
              <QRCodeSVG value={qrCode} size={200} />
            </Paper>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Can't scan the QR code? Enter this key manually:
            </Typography>
            <Box display="flex" gap={1} alignItems="center">
              <TextField
                fullWidth
                value={manualKey}
                InputProps={{
                  readOnly: true,
                  sx: { fontFamily: 'monospace', fontSize: '0.9rem' },
                }}
                size="small"
              />
              <Button
                variant="outlined"
                onClick={handleCopyKey}
                startIcon={copiedKey ? <CheckCircleIcon /> : <ContentCopyIcon />}
              >
                {copiedKey ? 'Copied!' : 'Copy'}
              </Button>
            </Box>
          </Box>
        )}

        {/* Step 2: Verify Code */}
        {activeStep === 2 && (
          <Box>
            <Typography variant="body1" gutterBottom>
              Enter the 6-digit code from your authenticator app:
            </Typography>
            <TextField
              fullWidth
              label="Verification Code"
              value={verificationCode}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                setVerificationCode(value);
              }}
              placeholder="123456"
              inputProps={{
                maxLength: 6,
                pattern: '[0-9]*',
                inputMode: 'numeric',
                style: { fontSize: '1.5rem', textAlign: 'center', letterSpacing: '0.5rem' },
              }}
              sx={{ my: 2 }}
              autoFocus
            />
            <Alert severity="info">
              The code changes every 30 seconds. Enter the current code shown in your app.
            </Alert>
          </Box>
        )}

        {/* Step 3: Backup Codes */}
        {activeStep === 3 && backupCodes.length > 0 && (
          <Box>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Save these backup codes in a secure location!
              </Typography>
              <Typography variant="body2">
                You can use these codes to access your account if you lose your phone.
                Each code can only be used once.
              </Typography>
            </Alert>

            <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50', mb: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle2">
                  Backup Codes ({backupCodes.length})
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleCopyBackupCodes}
                  startIcon={copiedCodes ? <CheckCircleIcon /> : <ContentCopyIcon />}
                >
                  {copiedCodes ? 'Copied!' : 'Copy All'}
                </Button>
              </Box>
              <Box
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '0.9rem',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: 1,
                }}
              >
                {backupCodes.map((code, index) => (
                  <Chip
                    key={index}
                    label={code}
                    sx={{ fontFamily: 'monospace' }}
                  />
                ))}
              </Box>
            </Paper>

            <Alert severity="success">
              <Typography variant="body2">
                Two-factor authentication is now enabled! You'll need to enter a code from your
                authenticator app each time you log in.
              </Typography>
            </Alert>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {activeStep < 3 && (
          <Button onClick={handleClose} disabled={setupMutation.isPending || verifyMutation.isPending}>
            Cancel
          </Button>
        )}

        {activeStep === 0 && (
          <Button
            variant="contained"
            onClick={handleSetup}
            disabled={setupMutation.isPending}
          >
            {setupMutation.isPending ? 'Setting up...' : 'Continue'}
          </Button>
        )}

        {activeStep === 1 && (
          <Button
            variant="contained"
            onClick={() => setActiveStep(2)}
          >
            I've Scanned the Code
          </Button>
        )}

        {activeStep === 2 && (
          <Button
            variant="contained"
            onClick={handleVerify}
            disabled={verificationCode.length !== 6 || verifyMutation.isPending}
          >
            {verifyMutation.isPending ? 'Verifying...' : 'Verify & Enable MFA'}
          </Button>
        )}

        {activeStep === 3 && (
          <Button
            variant="contained"
            onClick={handleComplete}
            color="success"
          >
            I've Saved My Backup Codes
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
